import { useMemo, useState } from "react";
import {
  Check,
  ChevronRight,
  Copy,
  Image as ImageIcon,
  X as XIcon,
  Brain,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Pill } from "@/components/ui/Pill";
import { Tooltip } from "@/components/ui/Tooltip";
import { Switch } from "@/components/ui/Switch";
import { stepScreenshotUrl } from "@/lib/api";
import { S } from "@/lib/strings";
import { cn, describeToolCall, diffObservation, formatDuration, formatTokens } from "@/lib/utils";
import { stepFailed, stepTotalTokens, type TimelineStep } from "@/lib/steps";
import { TERMINAL_TOOLS, type ActionResult } from "@/types/fleet";

/**
 * One step, live or historical — the same component either way.
 *
 * There is no separate "live step" renderer: a live step is just a step with
 * fewer fields present, and having two components would guarantee they drift
 * the moment someone changes the layout of one.
 */
export function StepCard({
  step,
  previous,
  runId,
  elapsedMs,
}: {
  step: TimelineStep;
  /** Step n−1, for the observation diff. */
  previous: TimelineStep | undefined;
  runId: string;
  elapsedMs: number | null;
}) {
  const failed = stepFailed(step);
  // A failure is the reason the operator opened this page. It opens itself.
  const [open, setOpen] = useState(failed);
  const [showObservation, setShowObservation] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const [onlyChanges, setOnlyChanges] = useState(true);

  const { toolCalls, results } = step;

  const resultFor = useMemo(() => {
    // Results are "in execution order" and tool calls are too, so pair by
    // position; fall back to matching on the tool name when the lengths differ
    // (a step that failed early has fewer results than calls).
    const map = new Map<number, ActionResult>();
    const taken = new Set<ActionResult>();
    toolCalls.forEach((call, i) => {
      const byIndex = results[i];
      if (byIndex && (byIndex.tool === call.tool || byIndex.tool === "?")) {
        map.set(i, byIndex);
        taken.add(byIndex);
        return;
      }
      const byName = results.find((r) => r.tool === call.tool && !taken.has(r));
      if (byName) {
        map.set(i, byName);
        taken.add(byName);
      }
    });
    return map;
  }, [toolCalls, results]);

  const tokens = stepTotalTokens(step);

  return (
    <div
      className={cn(
        "rounded-lg border bg-[rgba(255,255,255,0.02)] transition-colors",
        failed ? "border-[rgba(239,68,68,0.28)]" : "border-[var(--color-border-subtle)]",
      )}
    >
      {/* ---- header ---- */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-2">
        <span className="font-mono text-[13px] font-medium tabular-nums text-[var(--color-fg)]">
          #{step.n}
        </span>

        {step.thinking && (
          <Tooltip content="這一步啟用了 thinking">
            <Pill variant="info" size="sm" dot={false}>
              <Brain size={9} />
              {S.thinkingBadge}
            </Pill>
          </Tooltip>
        )}

        {step.live && (
          <Pill variant="warn" size="sm">
            {S.liveTail}
          </Pill>
        )}

        <div className="ml-auto flex items-center gap-3 font-mono text-[11px] text-[var(--color-fg-faint)]">
          {elapsedMs !== null && (
            <Tooltip content="與上一步之間的實際時間">
              <span className="tabular-nums">+{formatDuration(elapsedMs)}</span>
            </Tooltip>
          )}
          {step.latencyMs !== null && (
            <Tooltip
              content={
                step.ttftMs
                  ? `${S.latency} ${formatDuration(step.latencyMs)} · ${S.ttft} ${formatDuration(step.ttftMs)}`
                  : `${S.latency} ${formatDuration(step.latencyMs)}`
              }
            >
              <span className="tabular-nums">{formatDuration(step.latencyMs)}</span>
            </Tooltip>
          )}
          {tokens > 0 && (
            <Tooltip
              content={
                step.tokens
                  ? `prompt ${step.tokens.prompt} · completion ${step.tokens.completion} · reasoning ${step.tokens.reasoning}`
                  : undefined
              }
            >
              <span className="tabular-nums">{formatTokens(tokens)} tok</span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* ---- tool calls, one row each ---- */}
      <div className="border-t border-[var(--color-border-subtle)] px-3 py-1.5">
        {toolCalls.length === 0 && step.line && (
          <div className="py-0.5 font-mono text-[12px] text-[var(--color-fg-muted)]">{step.line}</div>
        )}

        {toolCalls.map((call, i) => {
          const result = resultFor.get(i);
          const ok = result?.ok !== false;
          const terminal = TERMINAL_TOOLS.includes(call.tool);
          return (
            <div
              key={`${call.tool}-${i}`}
              className="flex items-start gap-2 py-0.5 font-mono text-[12px] leading-relaxed"
            >
              {result ? (
                ok ? (
                  <Check size={12} className="mt-1 shrink-0 text-[#10b981]" aria-label={S.toolResultOk} />
                ) : (
                  <XIcon size={12} className="mt-1 shrink-0 text-[#ef4444]" aria-label={S.toolResultFail} />
                )
              ) : (
                <span className="mt-1 size-3 shrink-0" />
              )}

              <span
                className={cn(
                  "min-w-0 break-all",
                  terminal ? "text-[var(--color-fg)]" : "text-[var(--color-fg-muted)]",
                  !ok && "text-[#fca5a5]",
                )}
              >
                {describeToolCall(call)}
              </span>

              {result && !ok && (
                <span className="ml-auto shrink-0 pl-2 text-[11px] text-[#fca5a5]">
                  {result.errorCode ?? S.toolResultFail}
                </span>
              )}
            </div>
          );
        })}

        {/* A failed call's message is the actual diagnosis; show it inline. */}
        {results
          .filter((r) => !r.ok && r.message)
          .map((r, i) => (
            <div
              key={`err-${i}`}
              className="mt-1 rounded border border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.06)] px-2 py-1 font-mono text-[11px] text-[#fca5a5]"
            >
              {r.errorCode && <span className="mr-1.5 font-medium">{r.errorCode}</span>}
              {r.message}
            </div>
          ))}
      </div>

      {/* ---- collapsibles ---- */}
      {(step.observationText || step.reasoningText || step.screenshotPath) && (
        <div className="border-t border-[var(--color-border-subtle)]">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[11px] text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
          >
            <ChevronRight size={11} className={cn("transition-transform", open && "rotate-90")} />
            {open ? S.showLess : S.showMore}
            <span className="ml-1 text-[var(--color-fg-faint)]">
              {[
                step.observationText ? S.observation : null,
                step.reasoningText ? S.reasoning : null,
                step.screenshotPath ? S.screenshot : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </button>

          <div hidden={!open} className="space-y-2 px-3 pb-3">
            {step.screenshotPath && (
              <Thumbnail runId={runId} n={step.n} />
            )}

            {step.observationText && (
              <Collapsible
                icon={Eye}
                label={S.observation}
                open={showObservation}
                onToggle={() => setShowObservation((v) => !v)}
                right={
                  showObservation && (
                    <div className="flex items-center gap-3">
                      <Tooltip content={S.onlyChangesHint}>
                        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[var(--color-fg-subtle)]">
                          <Switch checked={onlyChanges} onCheckedChange={setOnlyChanges} />
                          {S.onlyChanges}
                        </label>
                      </Tooltip>
                      <CopyButton text={step.observationText} />
                    </div>
                  )
                }
              >
                <ObservationBlock
                  text={step.observationText}
                  previous={previous?.observationText ?? null}
                  onlyChanges={onlyChanges}
                />
              </Collapsible>
            )}

            {step.reasoningText && (
              <Collapsible
                icon={Brain}
                label={S.reasoning}
                open={showReasoning}
                onToggle={() => setShowReasoning((v) => !v)}
                right={showReasoning && <CopyButton text={step.reasoningText} />}
              >
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded bg-[rgba(0,0,0,0.25)] p-2 font-mono text-[11px] leading-relaxed text-[var(--color-fg-muted)]">
                  {step.reasoningText}
                </pre>
              </Collapsible>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Observation with the diff toggle
 * ------------------------------------------------------------------ */

function ObservationBlock({
  text,
  previous,
  onlyChanges,
}: {
  text: string;
  previous: string | null;
  onlyChanges: boolean;
}) {
  const lines = useMemo(() => diffObservation(text, previous), [text, previous]);
  const changedCount = lines.filter((l) => l.changed).length;

  return (
    <div>
      {previous && (
        <div className="mb-1 text-[10px] text-[var(--color-fg-faint)]">
          {changedCount} / {lines.length} {S.schemaLines}有變化
        </div>
      )}
      <pre className="max-h-96 overflow-auto rounded bg-[rgba(0,0,0,0.25)] p-2 font-mono text-[11px] leading-relaxed">
        {lines.map((l) => (
          <div
            key={l.lineNo}
            className={cn(
              "whitespace-pre-wrap",
              // 30 of 40 lines repeat verbatim from the previous step. Fading
              // them instead of hiding them keeps the screen's structure legible
              // while the eye goes straight to what moved.
              onlyChanges && previous && !l.changed
                ? "text-[var(--color-fg-faint)] opacity-40"
                : "text-[var(--color-fg-muted)]",
            )}
          >
            {l.text || " "}
          </div>
        ))}
      </pre>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Small parts
 * ------------------------------------------------------------------ */

function Collapsible({
  icon: Icon,
  label,
  open,
  onToggle,
  right,
  children,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  open: boolean;
  onToggle: () => void;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border border-[var(--color-border-subtle)]">
      <div className="flex items-center gap-2 px-2 py-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
        >
          <ChevronRight size={10} className={cn("transition-transform", open && "rotate-90")} />
          <Icon size={11} />
          {label}
        </button>
        <div className="ml-auto">{right}</div>
      </div>
      <div hidden={!open} className="px-2 pb-2">
        {children}
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => toast.success(S.copied))
          .catch(() => toast.error(S.error));
      }}
      className="flex items-center gap-1 text-[11px] text-[var(--color-fg-subtle)] transition-colors hover:text-[var(--color-fg)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
    >
      <Copy size={11} />
      {S.copy}
    </button>
  );
}

function Thumbnail({ runId, n }: { runId: string; n: number }) {
  const [failed, setFailed] = useState(false);
  const [full, setFull] = useState(false);
  const src = stepScreenshotUrl(runId, n);

  if (failed) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-[var(--color-fg-faint)]">
        <ImageIcon size={11} />
        {S.noScreenshot}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setFull((f) => !f)}
      className="block overflow-hidden rounded border border-[var(--color-border)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
      aria-label={`${S.screenshot} #${n}`}
    >
      <img
        src={src}
        alt={`${S.screenshot} step ${n}`}
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn(
          "bg-black object-contain transition-all",
          full ? "max-h-[70vh] w-auto" : "h-32 w-auto",
        )}
      />
    </button>
  );
}
